import { html, property, state } from 'lit-element'
import { SVG_MESSAGE_DOTS_OUTLINED } from '@getgo/chameleon-icons'
import { unsafeSVG } from 'lit-html/directives/unsafe-svg'
import type { PresenceEvents, PresenceUpdated } from '../../services/presence'
import { PresenceServiceNamespace } from '../../services/presence'
import { getShellApiInstance, getShellAnalytics } from '../../common/shell-api-helpers'
import { getShellLogger } from '../../common/logger'
import type { Listener } from '../../services/namespaces'
import { nothing } from 'lit-html'
import userNoteIconStyles from './user-note-icon.styles.scss'
import { ZIndex } from '../../common/z-index'
import { PopoverPosition, RoundButtonSize, RoundButtonVariant } from '@getgo/chameleon-core'
import { ifDefined } from 'lit-html/directives/if-defined'
import { ShellElement } from '../../common/shell-element'
import type { PresenceNamespace } from '../../services/namespaces/presence-namespace'
import { PRESENCE_NAMESPACE } from '../../services/namespaces/presence-namespace'

export class GotoUserNoteIcon extends ShellElement {
  static readonly tagName = 'goto-user-note-icon'
  static get styles() {
    return [userNoteIconStyles]
  }
  private _externalUserKey = ''
  @property({ type: String }) set externalUserKey(externalUserKey: string) {
    if (externalUserKey !== this._externalUserKey) {
      const previousValue = this._externalUserKey
      if (previousValue && this.isConnected) {
        this.unsubscribe()
      }
      this._externalUserKey = externalUserKey
      if (this.isConnected) {
        this.addListenerAndSubscribe()
      }
      this.requestUpdate('externalUserKey', previousValue)
    }
  }
  @property({ type: String }) tooltipPosition: PopoverPosition = 'bottom-center'
  @property({ type: String }) tooltipZIndex = ZIndex.tooltip
  @property({ type: String }) buttonSize: RoundButtonSize = 'small'
  @property({ type: String }) buttonVariant: RoundButtonVariant = 'secondary'
  @property({ type: Boolean }) canEditStatus = false
  @state() private userNote? = ''
  private readonly currentUserExternalUserKey = getShellApiInstance().user.key ?? ''

  connectedCallback() {
    super.connectedCallback()
    this.addListenerAndSubscribe()
  }

  private addListenerAndSubscribe() {
    if (this._externalUserKey) {
      this.listenForPresenceUpdatedEvent()
      this.subscribeToPresence()
    }
  }

  private subscribeToPresence() {
    const { presence } = getShellApiInstance()
    if (presence) {
      presence
        .subscribe(this._externalUserKey)
        .then(() => {
          this.unsubscribeFunctions.push(() => {
            presence.unsubscribe(this._externalUserKey)
          })
        })
        .catch(() => {
          getShellLogger().error(`failed to subscribe to presence for external user key ${this._externalUserKey}`)
        })
    }
  }

  private listenForPresenceUpdatedEvent() {
    const { eventBus } = getShellApiInstance()
    const { presenceUpdate } = eventBus.subscribeTo<typeof PresenceServiceNamespace, typeof PresenceEvents>(
      PresenceServiceNamespace,
    )

    const presenceUpdatedHandler: Listener<PresenceUpdated> = (payload: PresenceUpdated) => {
      const { externalUserKey, userNote } = payload
      if (this._externalUserKey === externalUserKey) {
        this.userNote = userNote
      }
    }

    presenceUpdate.on(presenceUpdatedHandler)

    this.unsubscribeFunctions.push(() => {
      presenceUpdate.removeListener(presenceUpdatedHandler)
    })
  }

  private getUserNoteIconButtonTemplate(slot?: string) {
    return html`<chameleon-round-button
      class="user-note-icon-behind-avatar"
      size=${this.buttonSize}
      variant=${this.buttonVariant}
      @click=${() => {
        // Mixpanel; Open Custom Status modal
        getShellAnalytics().track('GoTo > Custom Status Modal', { action: 'Click', event: 'Open', from: 'Topbar' })
        getShellApiInstance()
          .namespaces.retrieve<PresenceNamespace>(PRESENCE_NAMESPACE)
          .commands.openPresenceNoteDialog.execute()
      }}
      slot=${ifDefined(slot)}
      inactive
    >
      ${this.getSVGChatDotInactiveTemplate()}
    </chameleon-round-button>`
  }

  private getSVGChatDotInactiveTemplate(slot?: string) {
    return html` <chameleon-svg slot=${ifDefined(slot)}>${unsafeSVG(SVG_MESSAGE_DOTS_OUTLINED)}</chameleon-svg> `
  }

  render() {
    return this.userNote
      ? html`<chameleon-tooltip-v2 part="icon" position=${this.tooltipPosition} z-index=${this.tooltipZIndex}>
          ${this.currentUserExternalUserKey === this._externalUserKey && this.canEditStatus
            ? this.getUserNoteIconButtonTemplate('trigger')
            : this.getSVGChatDotInactiveTemplate('trigger')}
          <span slot="content">${this.userNote}</span>
        </chameleon-tooltip-v2>`
      : nothing
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GotoUserNoteIcon.tagName]: GotoUserNoteIcon
  }
}
